// WeatherWidgetComponent.tsx - Widget de Previsão do Tempo
// Exibe condições climáticas para ajudar pescadores a planejar viagens

import React, { useState, useEffect } from 'react';
import { 
  Sun, Cloud, CloudRain, CloudSnow, Wind, Droplets, 
  Thermometer, Eye, Sunrise, Sunset, MapPin, RefreshCw,
  CloudLightning, CloudFog, Waves
} from 'lucide-react';

interface WeatherData {
  location: string;
  current: {
    temp: number;
    feelsLike: number;
    humidity: number;
    windSpeed: number;
    windDirection: string;
    visibility: number;
    uvIndex: number;
    condition: WeatherCondition;
    description: string;
  };
  forecast: Array<{
    date: string;
    dayName: string;
    high: number;
    low: number;
    condition: WeatherCondition;
    rainChance: number;
    sunrise?: string;
    sunset?: string;
  }>;
  fishingConditions: {
    rating: 'excelente' | 'bom' | 'regular' | 'ruim';
    tips: string[];
  };
  lastUpdated: string;
}

type WeatherCondition = 
  | 'sunny' 
  | 'partly_cloudy' 
  | 'cloudy' 
  | 'rain' 
  | 'storm' 
  | 'fog' 
  | 'snow';

interface WeatherWidgetData {
  title?: string;
  location: string;
  latitude?: number;
  longitude?: number;
  showForecast?: boolean;
  forecastDays?: number;
  showFishingTips?: boolean;
  showSunTimes?: boolean;
  apiKey?: string; // Para uso com API real (OpenWeatherMap, etc)
  refreshInterval?: number; // Em minutos
  style?: 'compact' | 'detailed' | 'card';
}

interface Props {
  data: WeatherWidgetData;
  settings?: {
    backgroundColor?: string;
    textColor?: string;
  };
}

// Ícones por condição
const WeatherIcon: React.FC<{ condition: WeatherCondition; size?: number }> = ({ condition, size = 48 }) => {
  const iconProps = { size, className: 'weather-icon' };
  
  switch (condition) {
    case 'sunny':
      return <Sun {...iconProps} className="text-yellow-500" />;
    case 'partly_cloudy':
      return <Cloud {...iconProps} className="text-gray-400" />;
    case 'cloudy':
      return <Cloud {...iconProps} className="text-gray-500" />;
    case 'rain':
      return <CloudRain {...iconProps} className="text-blue-500" />;
    case 'storm':
      return <CloudLightning {...iconProps} className="text-purple-500" />;
    case 'fog':
      return <CloudFog {...iconProps} className="text-gray-400" />;
    case 'snow':
      return <CloudSnow {...iconProps} className="text-blue-200" />;
    default:
      return <Sun {...iconProps} className="text-yellow-500" />;
  }
};

// Dados mock para demonstração
const generateMockWeatherData = (location: string): WeatherData => {
  const conditions: WeatherCondition[] = ['sunny', 'partly_cloudy', 'cloudy', 'rain'];
  const randomCondition = conditions[Math.floor(Math.random() * conditions.length)];
  const baseTemp = 25 + Math.floor(Math.random() * 10);

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const today = new Date();

  const forecast = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    return {
      date: date.toISOString().split('T')[0],
      dayName: i === 0 ? 'Hoje' : i === 1 ? 'Amanhã' : weekDays[date.getDay()],
      high: baseTemp + Math.floor(Math.random() * 5),
      low: baseTemp - 5 + Math.floor(Math.random() * 3),
      condition: conditions[Math.floor(Math.random() * conditions.length)],
      rainChance: Math.floor(Math.random() * 60),
      sunrise: '05:42',
      sunset: '18:23',
    };
  });

  // Determinar condições de pesca
  const windSpeed = 5 + Math.floor(Math.random() * 20);
  let fishingRating: 'excelente' | 'bom' | 'regular' | 'ruim';
  const tips: string[] = [];

  if (randomCondition === 'storm' || windSpeed > 20) {
    fishingRating = 'ruim';
    tips.push('Condições adversas para pesca');
    tips.push('Evite sair em dias de tempestade');
  } else if (randomCondition === 'rain' || windSpeed > 15) {
    fishingRating = 'regular';
    tips.push('Peixes podem estar menos ativos');
    tips.push('Prefira iscas de profundidade');
  } else if (randomCondition === 'partly_cloudy') {
    fishingRating = 'excelente';
    tips.push('Condições ideais! Tempo nublado estimula atividade');
    tips.push('Tucunarés e Pirarucus mais ativos');
    tips.push('Experimente iscas de superfície');
  } else {
    fishingRating = 'bom';
    tips.push('Sol forte - pesque nas primeiras horas');
    tips.push('Foque em áreas sombreadas');
  }

  return {
    location,
    current: {
      temp: baseTemp,
      feelsLike: baseTemp + 2,
      humidity: 60 + Math.floor(Math.random() * 30),
      windSpeed,
      windDirection: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.floor(Math.random() * 8)],
      visibility: 10,
      uvIndex: 6 + Math.floor(Math.random() * 5),
      condition: randomCondition,
      description: 
        randomCondition === 'sunny' ? 'Céu limpo' :
        randomCondition === 'partly_cloudy' ? 'Parcialmente nublado' :
        randomCondition === 'cloudy' ? 'Nublado' :
        randomCondition === 'rain' ? 'Chuva leve' :
        'Tempo variável',
    },
    forecast,
    fishingConditions: {
      rating: fishingRating,
      tips,
    },
    lastUpdated: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
  };
};

export const WeatherWidgetComponent: React.FC<Props> = ({ data, settings }) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    title = 'Previsão do Tempo',
    location = 'Amazônia, Brasil',
    showForecast = true,
    forecastDays = 5,
    showFishingTips = true,
    showSunTimes = true,
    refreshInterval = 30,
    style = 'detailed',
  } = data;

  const fetchWeather = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Simular delay de API
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // TODO: Substituir por chamada real à API
      // const response = await fetch(`https://api.openweathermap.org/...`);
      // const data = await response.json();
      
      const mockData = generateMockWeatherData(location);
      setWeather(mockData);
    } catch (err) {
      setError('Não foi possível carregar a previsão do tempo');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWeather();

    // Atualizar periodicamente
    const interval = setInterval(fetchWeather, refreshInterval * 60 * 1000);
    return () => clearInterval(interval);
  }, [location, refreshInterval]);

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'excelente': return 'bg-green-500';
      case 'bom': return 'bg-blue-500';
      case 'regular': return 'bg-yellow-500';
      case 'ruim': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getRatingEmoji = (rating: string) => {
    switch (rating) {
      case 'excelente': return '🎣';
      case 'bom': return '👍';
      case 'regular': return '🤔';
      case 'ruim': return '⚠️';
      default: return '❓';
    }
  };

  if (isLoading && !weather) {
    return (
      <section 
        className="py-12 px-6"
        style={{ backgroundColor: settings?.backgroundColor }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48 mx-auto" />
            <div className="h-32 bg-gray-200 rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section 
        className="py-12 px-6"
        style={{ backgroundColor: settings?.backgroundColor }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-red-500">{error}</p>
          <button 
            onClick={fetchWeather}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Tentar novamente
          </button>
        </div>
      </section>
    );
  }

  if (!weather) return null;

  // Estilo Compacto
  if (style === 'compact') {
    return (
      <section 
        className="py-8 px-6"
        style={{ backgroundColor: settings?.backgroundColor, color: settings?.textColor }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <WeatherIcon condition={weather.current.condition} size={48} />
                <div>
                  <p className="text-4xl font-bold">{weather.current.temp}°C</p>
                  <p className="text-blue-100 flex items-center gap-1">
                    <MapPin size={14} />
                    {weather.location}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-1">
                  <Wind size={16} />
                  <span>{weather.current.windSpeed} km/h</span>
                </div>
                <div className="flex items-center gap-1">
                  <Droplets size={16} />
                  <span>{weather.current.humidity}%</span>
                </div>
                <div className={`px-3 py-1 rounded-full ${getRatingColor(weather.fishingConditions.rating)}`}>
                  {getRatingEmoji(weather.fishingConditions.rating)} Pesca: {weather.fishingConditions.rating}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Estilo Card
  if (style === 'card') {
    return (
      <section 
        className="py-12 px-6"
        style={{ backgroundColor: settings?.backgroundColor, color: settings?.textColor }}
      >
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 p-8 text-white text-center">
              <p className="flex items-center justify-center gap-2 text-blue-100 mb-2">
                <MapPin size={16} />
                {weather.location}
              </p>
              <WeatherIcon condition={weather.current.condition} size={80} />
              <p className="text-6xl font-light mt-4">{weather.current.temp}°</p>
              <p className="text-blue-100 capitalize">{weather.current.description}</p>
            </div>

            {/* Detalhes */}
            <div className="p-6 grid grid-cols-3 gap-4 text-center border-b">
              <div>
                <Droplets className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                <p className="text-xs text-gray-500">Umidade</p>
                <p className="font-semibold">{weather.current.humidity}%</p>
              </div>
              <div>
                <Wind className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                <p className="text-xs text-gray-500">Vento</p>
                <p className="font-semibold">{weather.current.windSpeed} km/h</p>
              </div>
              <div>
                <Thermometer className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                <p className="text-xs text-gray-500">Sensação</p>
                <p className="font-semibold">{weather.current.feelsLike}°</p>
              </div>
            </div>

            {/* Condições de Pesca */}
            {showFishingTips && (
              <div className="p-6">
                <div className={`${getRatingColor(weather.fishingConditions.rating)} text-white rounded-xl p-4 text-center`}>
                  <p className="text-2xl mb-1">{getRatingEmoji(weather.fishingConditions.rating)}</p>
                  <p className="font-semibold capitalize">Pesca {weather.fishingConditions.rating}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Estilo Detalhado (padrão)
  return (
    <section 
      className="py-16 px-6"
      style={{ backgroundColor: settings?.backgroundColor, color: settings?.textColor }}
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-500 flex items-center gap-2 mt-1">
              <MapPin size={16} />
              {weather.location}
            </p>
          </div>
          <button 
            onClick={fetchWeather}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Clima Atual */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-3xl p-8 text-white shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-7xl font-light">{weather.current.temp}°C</p>
                  <p className="text-xl text-blue-100 mt-2 capitalize">{weather.current.description}</p>
                  <p className="text-blue-200 mt-1">Sensação térmica: {weather.current.feelsLike}°C</p>
                </div>
                <div className="text-right">
                  <WeatherIcon condition={weather.current.condition} size={100} />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-blue-400/30">
                <div className="text-center">
                  <Wind className="w-6 h-6 mx-auto mb-2 text-blue-200" />
                  <p className="text-2xl font-semibold">{weather.current.windSpeed}</p>
                  <p className="text-sm text-blue-200">km/h {weather.current.windDirection}</p>
                </div>
                <div className="text-center">
                  <Droplets className="w-6 h-6 mx-auto mb-2 text-blue-200" />
                  <p className="text-2xl font-semibold">{weather.current.humidity}%</p>
                  <p className="text-sm text-blue-200">Umidade</p>
                </div>
                <div className="text-center">
                  <Eye className="w-6 h-6 mx-auto mb-2 text-blue-200" />
                  <p className="text-2xl font-semibold">{weather.current.visibility}</p>
                  <p className="text-sm text-blue-200">km visib.</p>
                </div>
                <div className="text-center">
                  <Sun className="w-6 h-6 mx-auto mb-2 text-blue-200" />
                  <p className="text-2xl font-semibold">{weather.current.uvIndex}</p>
                  <p className="text-sm text-blue-200">Índice UV</p>
                </div>
              </div>

              {showSunTimes && weather.forecast[0] && (
                <div className="flex items-center justify-center gap-8 mt-6 pt-4 border-t border-blue-400/30">
                  <div className="flex items-center gap-2">
                    <Sunrise className="w-5 h-5 text-yellow-300" />
                    <span className="text-blue-100">{weather.forecast[0].sunrise}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sunset className="w-5 h-5 text-orange-300" />
                    <span className="text-blue-100">{weather.forecast[0].sunset}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Condições de Pesca */}
          {showFishingTips && (
            <div className="bg-white rounded-3xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Waves className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold">Condições de Pesca</h3>
              </div>

              <div className={`${getRatingColor(weather.fishingConditions.rating)} text-white rounded-2xl p-6 text-center mb-6`}>
                <p className="text-4xl mb-2">{getRatingEmoji(weather.fishingConditions.rating)}</p>
                <p className="text-2xl font-bold capitalize">{weather.fishingConditions.rating}</p>
              </div>

              <div className="space-y-3">
                {weather.fishingConditions.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>{tip}</span>
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-400 mt-6 text-center">
                Atualizado às {weather.lastUpdated}
              </p>
            </div>
          )}
        </div>

        {/* Previsão Semanal */}
        {showForecast && (
          <div className="mt-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Próximos Dias</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-7 gap-4">
              {weather.forecast.slice(0, forecastDays).map((day, i) => (
                <div 
                  key={day.date}
                  className={`bg-white rounded-2xl p-4 text-center shadow-md hover:shadow-lg transition ${
                    i === 0 ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <p className="font-semibold text-gray-900">{day.dayName}</p>
                  <WeatherIcon condition={day.condition} size={32} />
                  <div className="mt-2">
                    <span className="text-lg font-bold text-gray-900">{day.high}°</span>
                    <span className="text-gray-400 ml-1">{day.low}°</span>
                  </div>
                  {day.rainChance > 0 && (
                    <p className="text-xs text-blue-500 mt-1 flex items-center justify-center gap-1">
                      <Droplets size={10} />
                      {day.rainChance}%
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Aviso */}
        <p className="text-center text-sm text-gray-400 mt-8">
          ⚠️ Previsão sujeita a alterações. Consulte fontes oficiais para planejamento de viagens.
        </p>
      </div>
    </section>
  );
};
